import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom'
import { getAllTeachers } from '../../../redux/teacherRelated/teacherHandle';
import { deleteUser } from '../../../redux/userRelated/userHandle';
import {
    Paper, Table, TableBody, TableContainer,
    TableHead, TablePagination, Button, Box, IconButton, Typography,
    Avatar, Tooltip, Divider, CircularProgress, Breadcrumbs, Link
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import * as XLSX from 'xlsx';
import { exportToExcel, getCurrentDateString } from '../../../utils/excelExport';
import styled, { keyframes } from 'styled-components';
import { useToast } from '../../../context/ToastContext';
import { StyledTableCell, StyledTableRow } from '../../../components/styles';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ShowTeachers = () => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { teachersList, loading,   response } = useSelector((state) => state.teacher);
    const { currentUser } = useSelector((state) => state.user);
    const location = useLocation();
    const fileInputRef = useRef(null);

    useEffect(() => {
        dispatch(getAllTeachers(currentUser._id));
    }, [currentUser._id, dispatch, location.pathname]);

    const { showToast } = useToast();
    const [importing, setImporting] = useState(false);

    const deleteHandler = (deleteID, address) => {
        dispatch({ type: 'teacher/getDeleteSuccess', payload: null });
        dispatch(deleteUser(deleteID, address)).then(() => {
            dispatch({ type: 'teacher/getSuccess', payload: [] });
            dispatch(getAllTeachers(currentUser._id));
            showToast("Teacher removed successfully", "success");
        });
    };

    const handleRefresh = () => {
        dispatch(getAllTeachers(currentUser._id));
    };

    const handleTeacherImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleDownloadTeacherTemplate = () => {
        const templateData = [
            {
                Name: 'Example Teacher',
                Email: 'teacher@example.com',
                Password: 'StrongPassword123',
                Phone: '9800000000',
                Address: 'Kathmandu, Nepal',
                Role: 'Teacher'
            }
        ];
        exportToExcel(templateData, 'Teacher_Import_Template', 'Teachers');
    };

    const handleTeacherImportFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';

        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

                if (!rows || rows.length === 0) {
                    showToast('The uploaded file contains no data.', 'warning');
                    return;
                }

                const teachers = rows.map((row) => ({
                    name: row.Name?.toString().trim(),
                    email: row.Email?.toString().trim(),
                    password: row.Password?.toString(),
                    phone: row.Phone?.toString().trim() || '',
                    address: row.Address?.toString().trim() || '',
                    role: row.Role?.toString().trim() || 'Teacher',
                }));

                setImporting(true);
                const response = await fetch(`${process.env.REACT_APP_BASE_URL}/Teachers/BulkCreate`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ school: currentUser._id, teachers })
                });

                const result = await response.json();
                if (!response.ok) {
                    showToast(result.message || 'Teacher import failed.', 'error');
                    return;
                }

                showToast(`Imported ${result.createdCount} teachers successfully.`, 'success');
                dispatch(getAllTeachers(currentUser._id));
            } catch (err) {
                console.error('Teacher import error:', err);
                showToast('Failed to import teachers.', 'error');
            } finally {
                setImporting(false);
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleExportTeachers = () => {
        if (!teachersList || teachersList.length === 0) return;

        const exportData = teachersList.map((teacher) => ({
            'Name': teacher.name,
            'Email': teacher.email || '-',
            'Phone': teacher.phone || 'Not Provided',
            'Address': teacher.address || 'Not Provided',
         }));

        exportToExcel(exportData, `Teachers_${getCurrentDateString()}`, 'Teachers');
    };

    const isEmpty = response || !teachersList || teachersList.length === 0;

    const PhotoCell = ({ photo, name }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Avatar 
                src={photo ? `${process.env.REACT_APP_BASE_URL}/${photo}` : ""} 
                sx={{ width: 44, height: 44, bgcolor: '#667eea', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
            >
                {!photo && name ? name.charAt(0) : <PersonIcon />}
            </Avatar>
        </Box>
    );

    return (
        <PageContainer>
            <MainContent>
                <HeaderSection>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 1 }}>
                            Teacher Management 👨‍🏫
                        </Typography>
                        <Breadcrumbs aria-label="breadcrumb">
                            <Link color="inherit" href="/Admin/dashboard">Dashboard</Link>
                            <Typography color="text.primary">Teachers</Typography>
                        </Breadcrumbs>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={handleRefresh}
                            sx={{ borderRadius: '10px' }}
                        >
                            Refresh
                        </Button>
                        <Button 
                            variant="contained" 
                            startIcon={<AddCircleIcon />}
                            onClick={() => navigate("/Admin/teachers/addteacher")}
                            sx={{ 
                                borderRadius: '12px',
                                px: 3,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                                '&:hover': {
                                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
                                }
                            }}
                        >
                            Add Teacher
                        </Button>
                    </Box>
                </HeaderSection>

                <ActionsBar elevation={1}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            startIcon={<UploadFileIcon />}
                            onClick={handleTeacherImportClick}
                            disabled={importing}
                            size="small"
                        >
                            Import
                        </Button>
                        <Button
                            startIcon={<FileDownloadIcon />}
                            onClick={handleDownloadTeacherTemplate}
                            size="small"
                        >
                            Template
                        </Button>
                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                        <Button
                            startIcon={<FileDownloadIcon />}
                            onClick={handleExportTeachers}
                            disabled={isEmpty}
                            size="small"
                        >
                            Export All
                        </Button>
                    </Box>
                    <input
                        type="file"
                        accept=".xlsx, .xls"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleTeacherImportFile}
                    />
                </ActionsBar>

                {loading ? (
                    <LoadingWrapper>
                        <CircularProgress size={60} thickness={4} />
                        <Typography sx={{ mt: 2, fontWeight: 500 }}>Fetching teachers...</Typography>
                    </LoadingWrapper>
                ) : isEmpty ? (
                    <EmptyState>
                        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>No Teachers Found</Typography>
                        <Typography color="textSecondary" sx={{ mb: 3 }}>
                            Add teachers manually or import them from an Excel file.
                        </Typography>
                        <Button 
                            variant="contained" 
                            onClick={() => navigate("/Admin/teachers/addteacher")}
                            sx={{ borderRadius: '10px', px: 4 }}
                        >
                            Add Your First Teacher
                        </Button>
                    </EmptyState>
                ) : (
                    <TablePaper>
                        <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <StyledTableRow>
                                        <StyledTableCell align="center">Photo</StyledTableCell>
                                        <StyledTableCell>Name</StyledTableCell>
                                        <StyledTableCell>Email</StyledTableCell>
                                        <StyledTableCell>Phone</StyledTableCell>
                                        <StyledTableCell>Address</StyledTableCell>
                                        <StyledTableCell align="center">Actions</StyledTableCell>
                                    </StyledTableRow>
                                </TableHead>
                                <TableBody>
                                    {teachersList
                                        .map((teacher) => (
                                            <StyledTableRow hover key={teacher._id}>
                                                <StyledTableCell align="center">
                                                    <PhotoCell photo={teacher.photo} name={teacher.name} />
                                                </StyledTableCell>
                                                <StyledTableCell sx={{ fontWeight: 600 }}>{teacher.name}</StyledTableCell>
                                                <StyledTableCell>{teacher.email}</StyledTableCell>
                                                <StyledTableCell>{teacher.phone || 'N/A'}</StyledTableCell>
                                                <StyledTableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {teacher.address || 'N/A'}
                                                </StyledTableCell>
                                                <StyledTableCell align="center">
                                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                                        <Tooltip title="View Details">
                                                            <IconButton 
                                                                onClick={() => navigate("/Admin/teachers/teacher/" + teacher._id)}
                                                                sx={{ color: '#667eea' }}
                                                            >
                                                                <VisibilityIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton 
                                                                onClick={() => deleteHandler(teacher._id, "Teacher")}
                                                                sx={{ color: '#ff4d4d' }}
                                                            >
                                                                <PersonRemoveIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        
                    </TablePaper>
                )}
            </MainContent>
        </PageContainer>
    );
};

export default ShowTeachers;

const PageContainer = styled.div`
  background-color: #f8f9fa;
  min-height: 100vh;
  padding: 24px;
`;

const MainContent = styled(Box)`
  max-width: 1400px;
  margin: 0 auto;
  animation: ${fadeIn} 0.6s ease-out;
`;

const HeaderSection = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 24px;
`;

const ActionsBar = styled(Paper)`
  padding: 12px 20px;
  border-radius: 12px !important;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  border: 1px solid #eee;
`;

const LoadingWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: #667eea;
`;

const EmptyState = styled(Paper)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 40px;
  border-radius: 20px;
  text-align: center;
  background: white;
  border: 1px dashed #ccc;
`;

const TablePaper = styled(Paper)`
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05) !important;
  border: 1px solid #eee;
`;